import { useContext, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useShiftSitesOverviewApiService } from "../services/api/ShiftSitesOverviewApiService";
import { useClientsApiService } from "../services/api/ClientsApiService";
import {
  ShiftSiteGroupOverview,
  ShiftSiteOverviewRow,
} from "../services/model/ShiftSite";
import { LocationSummary } from "../services/model/ShiftLocation";
import { Client } from "../services/model/Client";
import {
  DateRangeFilter,
  getDateRangeForFilter,
  isToday,
} from "../utils/dateRangeUtils";
import { getActiveShiftName } from "../utils/attendanceUtils";
import { AuthContext } from "../../../providers/AuthProvider";

interface RowStats {
  rostered: number;
  // Scheduled employee-days that have already elapsed: equals `rostered` for
  // live/past views, smaller when the range extends into the future. The
  // denominator attendance and adherence are judged against.
  rosteredToDate: number;
  // Live headcount — open clock-ins right now ("on site now"). Decays through
  // the day as shifts end; a secondary readout, NOT the adherence numerator.
  onSite: number;
  // Scheduled slots filled by anyone who attended (early-outs included),
  // capped at rostered. The adherence numerator — stable through the day and
  // identical to the site-detail's coverage count.
  covered: number;
  exceptions: number;
  lateArrival: number;
  noShow: number;
  adherence: number | null;
  activeShiftName: string | null;
}

interface SummaryStats {
  totalScheduled: number;
  totalScheduledToDate: number;
  totalClockedIn: number;
  totalNoShows: number;
  totalLateArrivals: number;
  regionsActive: number;
  totalRegions: number;
}

interface UseShiftSitesHierarchyOverviewResult {
  siteGroups: ShiftSiteGroupOverview[];
  ungrouped: LocationSummary[];
  isLoading: boolean;
  // True while a refetch is in flight even when `isLoading` is false (i.e.
  // the previous response is being held under `keepPreviousData`). Page
  // components use this to show a loading overlay on date-range changes.
  isFetching: boolean;
  // True when the data shown is the previous query's result held under
  // keepPreviousData (a background refetch is in flight). Used to suppress
  // the loading overlay on silent polls.
  isPreviousData: boolean;
  error: Error | null;
  dateRange: DateRangeFilter;
  setDateRange: (range: DateRangeFilter) => void;
  customRange: [Date | null, Date | null];
  setCustomRange: (range: [Date | null, Date | null]) => void;
  applyCustomRange: (from: Date, to: Date) => void;
  clientFilter: string | null;
  setClientFilter: (client: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  clients: Client[];
  dateBounds: { from: string; to: string };
  isTodayView: boolean;
  isSingleDay: boolean;
  getSiteRowStats: (site: ShiftSiteOverviewRow) => RowStats;
  getLocationRowStats: (location: LocationSummary) => RowStats;
  getGroupRowStats: (group: ShiftSiteGroupOverview) => RowStats;
  summaryStats: SummaryStats;
}

const useShiftSitesHierarchyOverview =
  (): UseShiftSitesHierarchyOverviewResult => {
    const auth = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateRange, setDateRange] = useState<DateRangeFilter>(
      (searchParams.get("range") as DateRangeFilter) || "today"
    );
    const [customRange, setCustomRange] = useState<[Date | null, Date | null]>([
      null,
      null,
    ]);
    const [clientFilter, setClientFilter] = useState<string | null>(
      searchParams.get("client") || null
    );
    const [searchQuery, setSearchQuery] = useState(
      searchParams.get("search") || ""
    );

    const dateBounds = useMemo(() => {
      if (dateRange === "custom" && customRange[0] && customRange[1]) {
        return {
          // Format in local time: toISOString() converts to UTC, which
          // shifts a local-midnight Date to the previous day.
          from: dayjs(customRange[0]).format("YYYY-MM-DD"),
          to: dayjs(customRange[1]).format("YYYY-MM-DD"),
        };
      }
      return getDateRangeForFilter(dateRange);
    }, [dateRange, customRange]);

    const isTodayView = isToday(dateRange, customRange);

    const applyCustomRange = (from: Date, to: Date): void => {
      setCustomRange([from, to]);
      setDateRange("custom");
    };

    // Sync filters to URL
    const updateSearchParams = (key: string, value: string | null): void => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      setSearchParams(params, { replace: true });
    };

    const handleSetDateRange = (range: DateRangeFilter): void => {
      setDateRange(range);
      updateSearchParams("range", range);
    };

    const handleSetClientFilter = (client: string | null): void => {
      setClientFilter(client);
      updateSearchParams("client", client);
    };

    const handleSetSearchQuery = (query: string): void => {
      setSearchQuery(query);
      updateSearchParams("search", query || null);
    };

    // API calls
    const {
      data: overviewData,
      isLoading,
      isFetching,
      isPreviousData,
      error,
    } = useShiftSitesOverviewApiService({
      employer: auth?.employer?.id,
      date_from: dateBounds.from,
      date_to: dateBounds.to,
      client: clientFilter || undefined,
      // Poll every 30s only for today views; rely on react-query's default
      // refetchIntervalInBackground:false so polling pauses when hidden.
      refetchInterval: isTodayView ? 30000 : false,
    });

    const { data: clientsData } = useClientsApiService({
      employer: auth?.employer?.id,
    });
    const clients = clientsData?.results || [];

    // Client-side search filtering
    const { siteGroups, ungrouped } = useMemo(() => {
      const allGroups = overviewData?.site_groups || [];
      const allUngrouped = overviewData?.ungrouped || [];

      if (!searchQuery.trim()) {
        return { siteGroups: allGroups, ungrouped: allUngrouped };
      }

      const q = searchQuery.toLowerCase();

      // Filter sites and direct locations within each group, remove empty groups
      const filteredGroups = allGroups
        .map((group) => {
          const filteredSites = group.sites.filter(
            (site) =>
              site.name.toLowerCase().includes(q) ||
              (site.client_name && site.client_name.toLowerCase().includes(q))
          );
          const filteredDirectLocs = (group.direct_locations || []).filter(
            (loc) =>
              loc.name.toLowerCase().includes(q) ||
              (loc.client_name && loc.client_name.toLowerCase().includes(q))
          );
          return {
            ...group,
            sites: filteredSites,
            direct_locations: filteredDirectLocs,
          };
        })
        .filter(
          (group) =>
            group.sites.length > 0 || (group.direct_locations?.length ?? 0) > 0
        );

      // Filter ungrouped locations
      const filteredUngrouped = allUngrouped.filter(
        (loc) =>
          loc.name.toLowerCase().includes(q) ||
          loc.region?.toLowerCase().includes(q) ||
          (loc.client_name && loc.client_name.toLowerCase().includes(q))
      );

      return { siteGroups: filteredGroups, ungrouped: filteredUngrouped };
    }, [overviewData, searchQuery]);

    // Only resolve an "active shift" for the live "today" view. A past day
    // (Yesterday, or any non-today range) has no shift running "now": its
    // per-shift stats count open clock-ins, which for a finished day collapse
    // to forgotten-clock-out / ghost rows. Historical views must show each
    // entity's whole-day backend totals (attended + slot-fill adherence).
    const isSingleDay = dateBounds.from === dateBounds.to;

    // Per-site row stats: resolves the site's own active shift from its own
    // `shift_configs` and reads `site.shift_name_stats[name]`. Falls back to
    // the site's top-level totals when no shift is active right now (or when
    // viewing anything other than today).
    const getSiteRowStats = (site: ShiftSiteOverviewRow): RowStats => {
      const shiftName = isTodayView
        ? getActiveShiftName(site.shift_configs)
        : null;
      const ss = shiftName ? site.shift_name_stats?.[shiftName] : undefined;
      if (ss) {
        const rostered = ss.employee_count;
        const onSite = ss.employees_on_site;
        // Coverage numerator: prefer the backend's record-based covered count
        // (counts early-outs, credits covering bodies); fall back to the live
        // headcount for payloads from before that field shipped.
        const covered = ss.covered ?? Math.min(onSite, rostered);
        return {
          rostered,
          rosteredToDate: rostered, // live single-day view: all slots elapsed
          onSite,
          covered,
          exceptions: ss.exception_summary?.total ?? 0,
          lateArrival: ss.exception_summary?.late_arrival ?? 0,
          noShow: ss.exception_summary?.no_show ?? 0,
          adherence:
            rostered > 0
              ? Math.round((Math.min(covered, rostered) / rostered) * 100)
              : null,
          activeShiftName: shiftName,
        };
      }
      return {
        rostered: site.rostered_employees,
        rosteredToDate: site.rostered_to_date ?? site.rostered_employees,
        onSite: site.on_site,
        // Historical / no-active-shift: the row shows the backend's clocked-in
        // (attended) figure as both the count and adherence basis, as before.
        covered: site.on_site,
        exceptions: site.exceptions,
        lateArrival: site.exception_summary?.late_arrival ?? 0,
        noShow: site.exception_summary?.no_show ?? 0,
        adherence: site.roster_adherence,
        activeShiftName: shiftName,
      };
    };

    // Per-location row stats: resolves the location's own active shift from
    // its own `shift_configs`, sums matching per-config entries in
    // `shift_stats`, and falls back to top-level totals when no shift
    // matches.
    const getLocationRowStats = (loc: LocationSummary): RowStats => {
      const shiftName = isTodayView
        ? getActiveShiftName(loc.shift_configs)
        : null;
      if (!shiftName || !loc.shift_stats || !loc.shift_configs) {
        return {
          rostered: loc.employee_count,
          rosteredToDate: loc.employee_count_to_date ?? loc.employee_count,
          onSite: loc.employees_on_site,
          covered: loc.employees_on_site,
          exceptions: loc.exception_summary?.total ?? 0,
          lateArrival: loc.exception_summary?.late_arrival ?? 0,
          noShow: loc.exception_summary?.no_show ?? 0,
          adherence: loc.coverage_percent,
          activeShiftName: shiftName,
        };
      }
      const matchingIds = loc.shift_configs
        .filter((c) => c.name === shiftName)
        .map((c) => c.id);
      const fallback: RowStats = {
        rostered: loc.employee_count,
        rosteredToDate: loc.employee_count_to_date ?? loc.employee_count,
        onSite: loc.employees_on_site,
        covered: loc.employees_on_site,
        exceptions: loc.exception_summary?.total ?? 0,
        lateArrival: loc.exception_summary?.late_arrival ?? 0,
        noShow: loc.exception_summary?.no_show ?? 0,
        adherence: loc.coverage_percent,
        activeShiftName: shiftName,
      };
      if (matchingIds.length === 0) return fallback;
      let rostered = 0;
      let onSite = 0;
      let exceptions = 0;
      let lateArrival = 0;
      let noShow = 0;
      let foundStats = false;
      matchingIds.forEach((id) => {
        const s = loc.shift_stats[id];
        if (!s) return;
        foundStats = true;
        rostered += s.employee_count || 0;
        onSite += s.employees_on_site || 0;
        exceptions += s.exception_summary?.total || 0;
        lateArrival += s.exception_summary?.late_arrival || 0;
        noShow += s.exception_summary?.no_show || 0;
      });
      if (!foundStats) return fallback;
      return {
        rostered,
        rosteredToDate: rostered, // live single-day view: all slots elapsed
        onSite,
        // Direct (Case 1) locations have no record-based covered count yet;
        // keep the live-headcount basis here (tracked as a follow-up).
        covered: onSite,
        exceptions,
        lateArrival,
        noShow,
        adherence:
          rostered > 0
            ? Math.round((Math.min(onSite, rostered) / rostered) * 100)
            : null,
        activeShiftName: shiftName,
      };
    };

    // Group row stats: aggregate whatever each child (site + direct_location)
    // is displaying, using their per-entity active shift. This keeps the
    // group total equal to the sum of the rows shown beneath it.
    const getGroupRowStats = (group: ShiftSiteGroupOverview): RowStats => {
      let rostered = 0;
      let rosteredToDate = 0;
      let onSite = 0;
      let covered = 0;
      let exceptions = 0;
      let lateArrival = 0;
      let noShow = 0;
      // Aggregate child adherence as covered slots over ELAPSED rostered
      // slots: with min(onSite, rostered) over the summed totals, a surplus
      // at one site masked a shortfall at another and pinned the group row
      // at 100% — and weighting by the full-range rostered count would let
      // future, not-yet-judgeable days dilute the average.
      let filledSlots = 0;
      let adherenceRostered = 0;
      const tally = (rs: RowStats): void => {
        rostered += rs.rostered;
        rosteredToDate += rs.rosteredToDate;
        onSite += rs.onSite;
        covered += rs.covered;
        exceptions += rs.exceptions;
        lateArrival += rs.lateArrival;
        noShow += rs.noShow;
        if (rs.adherence !== null && rs.rosteredToDate > 0) {
          adherenceRostered += rs.rosteredToDate;
          filledSlots += (rs.adherence / 100) * rs.rosteredToDate;
        }
      };
      group.sites.forEach((s) => tally(getSiteRowStats(s)));
      (group.direct_locations || []).forEach((loc) =>
        tally(getLocationRowStats(loc))
      );
      return {
        rostered,
        rosteredToDate,
        onSite,
        covered,
        exceptions,
        lateArrival,
        noShow,
        adherence:
          adherenceRostered > 0
            ? Math.round((filledSlots / adherenceRostered) * 100)
            : null,
        activeShiftName: null,
      };
    };

    // Summary-card aggregates: sum per-site/per-location active-shift stats
    // across everything visible. Helpers above close over `isTodayView`, so
    // results stay consistent with the table rows.
    let totalScheduled = 0;
    let totalScheduledToDate = 0;
    let totalClockedIn = 0;
    let totalNoShows = 0;
    let totalLateArrivals = 0;

    const countSite = (s: ShiftSiteOverviewRow): void => {
      const rs = getSiteRowStats(s);
      totalScheduled += rs.rostered;
      totalScheduledToDate += rs.rosteredToDate;
      totalClockedIn += rs.covered;
      totalNoShows += rs.noShow;
      totalLateArrivals += rs.lateArrival;
    };
    const countLoc = (loc: LocationSummary): void => {
      const rs = getLocationRowStats(loc);
      totalScheduled += rs.rostered;
      totalScheduledToDate += rs.rosteredToDate;
      totalClockedIn += rs.covered;
      totalNoShows += rs.noShow;
      totalLateArrivals += rs.lateArrival;
    };
    siteGroups.forEach((g) => {
      g.sites.forEach(countSite);
      (g.direct_locations || []).forEach(countLoc);
    });
    ungrouped.forEach(countLoc);

    const allDirectLocationsFlat = siteGroups.flatMap(
      (g) => g.direct_locations || []
    );
    const allSitesFlat = siteGroups.flatMap((g) => g.sites);
    const totalRegions =
      allSitesFlat.length + allDirectLocationsFlat.length + ungrouped.length;
    const regionsActive =
      allSitesFlat.filter((s) => getSiteRowStats(s).rostered > 0).length +
      allDirectLocationsFlat.filter(
        (loc) => getLocationRowStats(loc).rostered > 0
      ).length +
      ungrouped.filter((loc) => getLocationRowStats(loc).rostered > 0).length;

    const summaryStats: SummaryStats = {
      totalScheduled,
      totalScheduledToDate,
      totalClockedIn,
      totalNoShows,
      totalLateArrivals,
      regionsActive,
      totalRegions,
    };

    return {
      siteGroups,
      ungrouped,
      isLoading,
      isFetching,
      isPreviousData,
      error,
      dateRange,
      setDateRange: handleSetDateRange,
      customRange,
      setCustomRange,
      applyCustomRange,
      clientFilter,
      setClientFilter: handleSetClientFilter,
      searchQuery,
      setSearchQuery: handleSetSearchQuery,
      clients,
      dateBounds,
      isTodayView,
      isSingleDay,
      getSiteRowStats,
      getLocationRowStats,
      getGroupRowStats,
      summaryStats,
    };
  };

export default useShiftSitesHierarchyOverview;
